import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'lifelinke_push_dismissed';

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * usePushNotifications - hook to manage Web Push subscription
 * Returns:
 *  - supported: boolean
 *  - permission: 'default' | 'granted' | 'denied'
 *  - subscribed: boolean
 *  - subscribe(): request permission and subscribe
 *  - unsubscribe(): remove subscription
 *  - test(): send a test push
 */
export default function usePushNotifications() {
  const { user } = useAuth();
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(ok);
    if (ok) {
      setPermission(Notification.permission);
      navigator.serviceWorker.ready.then(async (reg) => {
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      }).catch(() => {});
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, error: 'Notifiche push non supportate' };
    if (!user) return { ok: false, error: 'Devi accedere prima' };
    setLoading(true);
    try {
      // Ask permission
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
      }
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return { ok: false, error: 'Permesso negato' };
      }

      // Get VAPID public key
      const { data: vapidData } = await axios.get(`${API}/push/vapid-public-key`);
      const applicationServerKey = urlBase64ToUint8Array(vapidData.public_key);

      // Subscribe to push manager
      const reg = await navigator.serviceWorker.ready;
      let pushSub = await reg.pushManager.getSubscription();
      if (!pushSub) {
        pushSub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      }

      // Send subscription to backend
      const token = localStorage.getItem('lifelink_token');
      const subJson = pushSub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSubscribed(true);
      setLoading(false);
      return { ok: true };
    } catch (e) {
      setLoading(false);
      return { ok: false, error: e.message };
    }
  }, [supported, user]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const token = localStorage.getItem('lifelink_token');
        const subJson = sub.toJSON();
        await axios.post(`${API}/push/unsubscribe`, {
          endpoint: subJson.endpoint,
          keys: subJson.keys || {},
        }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const test = useCallback(async () => {
    if (!supported || !subscribed) return { ok: false, error: 'Non sottoscritto' };
    const token = localStorage.getItem('lifelink_token');
    try {
      const { data } = await axios.post(`${API}/push/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || e.message };
    }
  }, [supported, subscribed]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe, test };
}

export { DISMISS_KEY };
